/**
 * Member-Card tap interactions (touch devices only).
 *
 * Desktop (`hover: hover`) — the CSS `:hover` / `:focus-within` rules in
 * `_members-section.scss` already handle the flip + text-reveal + hover-
 * colour transitions. No JS needed.
 *
 * Mobile / touch (`hover: none`) — tap a card to toggle a `.is-active`
 * class on the card; the same SCSS animations are wired to `.is-active`
 * (see `_members-section.scss`). One-at-a-time: tapping a second card
 * closes the first. Tap outside any card to close everything.
 *
 * The script is idempotent — runs once on `pageInit`-style entry, finds
 * cards by class, returns early if none on the page.
 */
export function memberCardTapInteractions() {
	// Touch detection — `(hover: none)` is unreliable in Chrome DevTools
	// mobile emulation and on hybrid devices. Sniffing `ontouchstart` /
	// `navigator.maxTouchPoints` is robust across both real phones AND
	// device-emulation. Hybrid laptops report touch capable AND fine-pointer
	// hover — we still enable the tap behaviour for them; the hover CSS
	// stays active in parallel, so either mode works.
	const isTouch = 'ontouchstart' in window
		|| navigator.maxTouchPoints > 0
		|| navigator.msMaxTouchPoints > 0;

	if ( ! isTouch ) {
		return;
	}

	// Flag the body so CSS can target touch contexts without relying on
	// `@media (hover: none)` — see `_members-section.scss`.
	document.body.classList.add( 'is-touch' );

	// `.member-card--tappable` is set in the template whenever a card has
	// tap-revealable content (member text OR fun-image) AND is not a CTA
	// tile. Earlier this filter was `--flippable + :not(--cta)`, but that
	// missed cards with only a member text (no fun-image) — those should
	// still respond to a tap to reveal the quote.
	const tappable = Array.from( document.querySelectorAll( '.member-card--tappable' ) );
	if ( tappable.length === 0 ) {
		return;
	}

	tappable.forEach( ( card ) => {
		card.addEventListener( 'click', ( event ) => {
			// Don't interfere with native link clicks inside the card.
			if ( event.target.closest( 'a' ) ) {
				return;
			}

			const wasActive = card.classList.contains( 'is-active' );

			// Close any other open card first.
			tappable.forEach( ( other ) => {
				if ( other !== card ) {
					other.classList.remove( 'is-active' );
				}
			} );

			// Toggle this card.
			card.classList.toggle( 'is-active', ! wasActive );

			// Sticky-dismiss flag: once a user has tapped a card, the
			// "Tippen"-hint has served its purpose. We mark the card
			// permanently so the SCSS keeps the hint hidden even after the
			// card is closed again — otherwise the badge would re-appear on
			// every close, confusing users who tap the fun-image to close
			// (they'd see the badge come back without anything to discover).
			card.classList.add( 'has-been-tapped' );
		} );
	} );

	// Tap outside any member-card closes the currently open one.
	document.addEventListener( 'click', ( event ) => {
		if ( ! event.target.closest( '.member-card--tappable' ) ) {
			tappable.forEach( ( card ) => card.classList.remove( 'is-active' ) );
		}
	} );
}
